import { useEffect, useState } from 'react';
import { kpisActions } from '../../kpiSlice';
import { useAppDispatch, useAppSelector } from '../../../../core/rootHooks';
import { AppDispatch, RootState } from '../../../../core/store';
import {
	phaseCountsOfGeneralSeaAreasSelector,
	phasePercentagesSelector,
	signalCountsOnSpeedRangesSelector,
	keyMetricsInfoSelector,
} from '../../kpiSelectors';
import OperationalPhaseChart from './OperationalPhaseChart';
import GeneralSeaAreaOperationalPhaseChart from './GeneralSeaAreaOperationalPhaseCountsChart';
import SpeedDistributionChart from './SpeedDistributionChart';
import KeyMetrics from './KeyMetrics';
import LoadedConditionChart from './LoadedConditionChart';
import { IKpiVessel, TKeyMetrics } from '../../kpiTypes';
import OperationalPhaseMap from '../vessel-maps/OperationalPhaseMap';
import { ITabPage } from '../../../../shared/components/tabs/ITabPage';
import LoadingSpinner from '../../../../shared/components/loading-spinner/LoadingSpinner';
import ExpandableContainer from '../../../../shared/components/expandable-container/ExpandableContainer';
import { greenActions, selectVesselKeyMetricsDateBased } from '../../../green/greenSlice';
import ImoRenderer from '../../../../shared/components/renderers/ImoRenderer';
import { selectFeatureDateRangeAnalysisDateRange } from '../../../date-range/slices/dateRangeSlice';
import { selectAnalysisDateRangeString } from '../../../date-range/dataRangeSelector';
import { calculateAdjustedSpeedMetricsForVessel } from '../../kpisHelpers';

const KpiOverview: React.FC<ITabPage<IKpiVessel>> = ({ ...props }) => {
	const dispatch = useAppDispatch<AppDispatch>();

	const vesselPhasePercentages = useAppSelector((state: RootState) => phasePercentagesSelector(state, props.data.imoNumber!));
	const vesselPhaseCountsOfGeneralSeaAreas = useAppSelector((state: RootState) =>
		phaseCountsOfGeneralSeaAreasSelector(state, props.data.imoNumber!),
	);
	const vesselSignalCountsOnSpeedRanges = useAppSelector((state: RootState) =>
		signalCountsOnSpeedRangesSelector(state, props.data.imoNumber!),
	);
	const vesselKeyMetrics = useAppSelector((state: RootState) => keyMetricsInfoSelector(state, props.data.imoNumber!));
	const vesselKeyMetricsDateBased = useAppSelector(selectVesselKeyMetricsDateBased);
	const analysisDateRange = useAppSelector(selectFeatureDateRangeAnalysisDateRange);
	const dateRangeString = useAppSelector(selectAnalysisDateRangeString);

	const [adjustedKeyMetrics, setAdjustedKeyMetrics] = useState<TKeyMetrics>();

	useEffect(() => {
		const adjustedMetrics = calculateAdjustedSpeedMetricsForVessel(vesselKeyMetrics, vesselSignalCountsOnSpeedRanges);

		if (adjustedMetrics) {
			setAdjustedKeyMetrics(adjustedMetrics);
		}
	}, [vesselKeyMetrics, vesselSignalCountsOnSpeedRanges]);

	useEffect(() => {
		if (props.data.imoNumber) {
			const startYearMonth = analysisDateRange.yearMonthRange?.start;
			const endYearMonth = analysisDateRange.yearMonthRange?.end;
			if (!startYearMonth || !endYearMonth) return;

			dispatch(kpisActions.getPhasePercentagesRequestAction({ imoNumber: props.data.imoNumber, startYearMonth, endYearMonth }));
		}
	}, [dispatch, props.data.imoNumber, analysisDateRange]);

	useEffect(() => {
		if (props.data.imoNumber) {
			const startYearMonth = analysisDateRange.yearMonthRange?.start;
			const endYearMonth = analysisDateRange.yearMonthRange?.end;
			if (!startYearMonth || !endYearMonth) return;
			dispatch(
				kpisActions.getPhaseCountsOfGeneralSeaAreasRequestAction({
					imoNumber: props.data.imoNumber,
					startYearMonth,
					endYearMonth,
				}),
			);
		}
	}, [dispatch, props.data.imoNumber, analysisDateRange]);

	useEffect(() => {
		if (props.data.imoNumber) {
			const startYearMonth = analysisDateRange.yearMonthRange?.start;
			const endYearMonth = analysisDateRange.yearMonthRange?.end;
			if (!startYearMonth || !endYearMonth) return;
			dispatch(
				kpisActions.getSignalCountsOnSpeedRangesRequestAction({ imoNumber: props.data.imoNumber, startYearMonth, endYearMonth }),
			);
		}
	}, [dispatch, props.data.imoNumber, analysisDateRange]);

	useEffect(() => {
		if (props.data.imoNumber) {
			const startYearMonth = analysisDateRange.yearMonthRange?.start;
			const endYearMonth = analysisDateRange.yearMonthRange?.end;
			if (!startYearMonth || !endYearMonth) return;
			dispatch(kpisActions.getVesselKeyMetricsRequestAction({ imoNumber: props.data.imoNumber, startYearMonth, endYearMonth }));
		}
	}, [dispatch, props.data.imoNumber, analysisDateRange]);

	useEffect(() => {
		if (props.data.imoNumber) {
			const startYearMonth = analysisDateRange.yearMonthRange?.start;
			const endYearMonth = analysisDateRange.yearMonthRange?.end;
			if (!startYearMonth || !endYearMonth) return;
			dispatch(
				greenActions.getVesselKeyMetricsDateBasedRequestAction({
					imoNumbers: [props.data.imoNumber],
					startYearMonth,
					endYearMonth,
				}),
			);
		}
	}, [dispatch, props.data.imoNumber, analysisDateRange]);

	const [panelOffset, setPanelOffset] = useState<{ top: number; left: number }>();
	const [refElement, setRefElement] = useState<HTMLDivElement | null>(null);

	const callbackRef = (node: HTMLDivElement | null) => {
		setRefElement(node);
	};

	useEffect(() => {
		const updatePanelSize = () => {
			if (refElement) {
				const { offsetTop, offsetLeft } = refElement;
				setPanelOffset({ top: offsetTop, left: offsetLeft });
			}
		};

		// Initial measurement
		updatePanelSize();

		// Update on window resize
		window.addEventListener('resize', updatePanelSize);
		return () => {
			window.removeEventListener('resize', updatePanelSize);
		};
	}, [refElement]);

	return (
		<>
			{vesselKeyMetrics === null ? (
				<LoadingSpinner size="md" spinnerClassName="p-2" />
			) : !vesselKeyMetrics.loading && vesselKeyMetrics && !vesselKeyMetrics.data ? (
				<div className="flex w-full h-full justify-center">
					<span>No signals found! (Overview)</span>
				</div>
			) : (
				<div ref={callbackRef} className="flex flex-col gap-2 w-full h-full">
					<div className="w-full flex-col">
						<span className="font-bold text-msi-midblue-color-500">
							{/* Display IMO renderer in order to link the vessel if isFleet, otherwise display name */}
							{(props.isFleet && (
								<ImoRenderer
									imoNumber={props.data.imoNumber!}
									displayValue={props.data.name}
									className="text-msi-midblue-color-500 font-bold text-sm"
								/>
							)) ||
								props.data.name}{' '}
							|{' '}
						</span>
						<span className="font-bold">Operational Profile : </span>
						<span>{dateRangeString}</span>
					</div>
					<div className="flex flex-row xl:flex-row gap-4 w-full h-full">
						<div className="flex flex-col w-2/5 h-full gap-4">
							<div id="phase" className="flex flex-row h-1/3 gap-4 ">
								<div className="border border-msi-darkgrey-color-300 rounded w-2/3">
									{!vesselPhasePercentages || (!vesselPhasePercentages.isFetched && vesselPhasePercentages?.loading) ? (
										<LoadingSpinner size="md" spinnerClassName="p-2" />
									) : (
										<ExpandableContainer title="Operational Phase" panelOffsets={panelOffset}>
											<OperationalPhaseChart
												chartKey={vesselPhasePercentages.imoNumber!}
												phasePercentagesData={vesselPhasePercentages.dataList!}
											/>
										</ExpandableContainer>
									)}
								</div>
								<div className="border border-msi-darkgrey-color-300 rounded w-1/3">
									{!vesselKeyMetrics || vesselKeyMetrics?.loading ? (
										<LoadingSpinner size="md" spinnerClassName="p-2" />
									) : (
										<ExpandableContainer title="% Ballast" panelOffsets={panelOffset}>
											<LoadedConditionChart chartKey={vesselKeyMetrics.imoNumber!} keyMetricsData={vesselKeyMetrics.data!} />
										</ExpandableContainer>
									)}
								</div>
							</div>
							<div id="generalSeaArea" className="flex flex-row h-1/3 border border-msi-darkgrey-color-300 rounded">
								{!vesselPhaseCountsOfGeneralSeaAreas || vesselPhaseCountsOfGeneralSeaAreas?.loading ? (
									<LoadingSpinner size="md" spinnerClassName="p-2" />
								) : (
									<ExpandableContainer title="Geographical Distribution (hours)" panelOffsets={panelOffset}>
										<GeneralSeaAreaOperationalPhaseChart
											chartKey={vesselPhaseCountsOfGeneralSeaAreas.imoNumber!}
											phaseCountsOfGeneralSeaAreasData={vesselPhaseCountsOfGeneralSeaAreas.dataList!}
										/>
									</ExpandableContainer>
								)}
							</div>
							<div id="speed" className="flex flex-row h-1/3 border border-msi-darkgrey-color-300 rounded">
								{!vesselSignalCountsOnSpeedRanges || vesselSignalCountsOnSpeedRanges?.loading ? (
									<LoadingSpinner size="md" spinnerClassName="p-2" />
								) : (
									<ExpandableContainer title="Speed Distribution (knots)" panelOffsets={panelOffset}>
										<SpeedDistributionChart
											chartKey={vesselSignalCountsOnSpeedRanges.imoNumber!}
											signalCountsOnSpeedRangesData={vesselSignalCountsOnSpeedRanges.dataList!}
										/>
									</ExpandableContainer>
								)}
							</div>
						</div>

						<div className="flex flex-col w-3/5 h-full">
							<div className="flex flex-col h-full w-full border border-msi-darkgrey-color-300 rounded p-1">
								<ExpandableContainer panelOffsets={panelOffset} hideTitleBar reverseIconColor>
									<OperationalPhaseMap imoNumber={props.data.imoNumber!} />
								</ExpandableContainer>
							</div>
						</div>

						<div className="flex flex-col w-1/5 h-full gap-4">
							<div className="w-full h-full border border-msi-darkgrey-color-300 rounded p-2 overflow-auto">
								{!adjustedKeyMetrics || adjustedKeyMetrics?.loading ? (
									<LoadingSpinner size="md" spinnerClassName="p-2" />
								) : (
									<KeyMetrics
										key={props.data.imoNumber!}
										keyMetricsData={adjustedKeyMetrics.data}
										keyMetricsDateBased={
											(vesselKeyMetricsDateBased &&
												vesselKeyMetricsDateBased.vesselKeyMetricsDateBasedData &&
												vesselKeyMetricsDateBased.vesselKeyMetricsDateBasedData.length > 0 &&
												vesselKeyMetricsDateBased.vesselKeyMetricsDateBasedData[0]) ||
											null
										}
									/>
								)}
							</div>
						</div>
					</div>
				</div>
			)}
		</>
	);
};

export default KpiOverview;
